import 'filepond-polyfill'
import 'promise-polyfill/src/polyfill'
import * as FilePond from 'filepond'
import FilePondPluginFileMetadata from 'filepond-plugin-file-metadata'
import FilePondPluginImageExifOrientation from 'filepond-plugin-image-exif-orientation'
import FilePondPluginImageCrop from 'filepond-plugin-image-crop'
import FilePondPluginImageResize from 'filepond-plugin-image-resize'
import FilePondPluginImagePreview from 'filepond-plugin-image-preview'
import FilePondPluginImageTransform from 'filepond-plugin-image-transform'
import FilePondPluginFileValidateType from 'filepond-plugin-file-validate-type'
import FilePondPluginFileValidateSize from 'filepond-plugin-file-validate-size'
import FilePondPluginImageValidateSize from 'filepond-plugin-image-validate-size'
import { getFiles } from './helpers/getFiles'
import { addFile, addOrUpdateFile } from './helpers/addFile'
import { deleteFile } from './helpers/deleteFile'
import { generateUniqueID } from './helpers/generateUniqueID'
import { prepareTmpFilesForPond } from './helpers/prepareTmpFilesForPond'
import { prepareSavedFilesForPond } from './helpers/prepareSavedFilesForPond'
import { getEntryId } from './helpers/getEntryId'
import { fieldOnCurrentPage } from './helpers/fieldOnCurrentPage'
import { getUrlParameter } from './helpers/getUrlParameter'
import { getImageSize } from './helpers/getImageSize'
import { removeMinimumWarningMessage, maybeAddMinimumWarningMessage } from './helpers/minimumImageSizeWarning'
import { reorderFiles } from './helpers/reorderFiles'
import keyboardNavigation from './helpers/keyboardNavigation'
import addTabIndex from './helpers/addTabIndex'
import { setImageSize } from './helpers/setImageSize'
import '../css/style.scss'

/**
 * @package     Image Hopper
 * @copyright   Copyright (c) 2021, Image Hopper
 * @license     http://opensource.org/licenses/gpl-2.0.php GNU Public License
 */

(($, gform) => {
  const pondStore = []
  const adminEntryPage = typeof adminpage !== 'undefined' && adminpage.indexOf('_page_gf_entries') > -1
  const l10n = image_hopper_js_strings.l10n
  const imageEndpoint = image_hopper_js_strings.process_endpoint

  $(document).on('gform_post_render', (event, formId, currentPage) => {
    /* Don't run if currently submitting the form */
    if (window['gf_submitting_' + formId]) {
      return
    }

    // clear up all active FilePond instances to reduce memory
    if (Array.isArray(pondStore[formId])) {
      pondStore[formId].forEach(pond => {
        pond.destroy()
      })
    } else {
      pondStore[formId] = []
    }

    // don't run if displaying current form's confirmation page
    if ($('#gform_confirmation_wrapper_' + formId).length > 0) {
      return
    }

    let $form = $('form#gform_' + formId)
    if ($form.length === 0) {
      $form = $('#gform_wrapper_' + formId).closest('form')
    }

    setUp($form, currentPage)
  })

  $(() => {
    if (adminEntryPage) {
      setUp($('form#entry_form'))
    }
  })

  function setUp ($form, currentPage) {
    let pendingUploads = 0

    // generate unique ID
    const $uid = $form.find('input:hidden[name="gform_unique_id"]')
    if ($uid.val() === '') {
      $uid.val(generateUniqueID())
    }

    // setup FilePond plugins
    FilePond.registerPlugin(
      FilePondPluginFileMetadata,
      FilePondPluginImageExifOrientation,
      FilePondPluginFileValidateType,
      FilePondPluginImageCrop,
      FilePondPluginImageResize,
      FilePondPluginFileValidateSize,
      FilePondPluginImageValidateSize,
      FilePondPluginImagePreview,
      FilePondPluginImageTransform
    )

    gform.doAction('image_hopper_filepond_plugin_registration', FilePond, $form, currentPage)

    // count the number of IH fields on a single page
    let imageHopperCounter = 0
    $form.find('.ginput_container_image_hopper input[type="file"]').each(function () {
      if (!fieldOnCurrentPage(this, currentPage, $(this).data('formId'))) {
        return
      }

      imageHopperCounter++
    })

    $form.find('.ginput_container_image_hopper input[type="file"]').each(function () {
      const formId = $(this).data('formId')
      const fieldId = $(this).data('fieldId')
      const entryId = getEntryId(this)
      const nonce = $(this).data('nonce')

      if (!Array.isArray(pondStore[formId])) {
        pondStore[formId] = []
      }

      // On the initial form load, prefill the #gform_uploaded_files_{form_id} source of truth
      let files = []
      if ((adminEntryPage || parseInt(currentPage) === 1) && getFiles(fieldId, formId).length === 0) {
        files = [...prepareTmpFilesForPond(getFiles(fieldId, formId)), ...prepareSavedFilesForPond($(this).data('files'))]
        files.forEach(item => {
          addFile(formId, fieldId, {
            temp_filename: '',
            uploaded_filename: item.source
          })
        })
      } else {
        // otherwise, load from the source of truth
        files = prepareTmpFilesForPond(getFiles(fieldId, formId))
      }

      // not currently displaying the page this field is shown on
      if (!fieldOnCurrentPage(this, currentPage, formId)) {
        return
      }

      // prevent form submitting while still uploading
      $form
        .off('submit', preventSubmissionIfStillUploading)
        .on('submit', { formId: formId }, preventSubmissionIfStillUploading)

      // Create the FilePond instance
      gform.doAction('image_hopper_pre_filepond_create', FilePond, $form, currentPage, formId, fieldId, entryId, files, this)

      const config = gform.applyFilters('image_hopper_filepond_config', {
        allowMultiple: true,
        allowReorder: true,
        allowPaste: imageHopperCounter === 1,
        credits: false,
        files: files,
        itemInsertLocation: 'after',
        styleItemPanelAspectRatio: 0.75,
        imageResizeMode: 'contain',
        imageTransformCanvasMemoryLimit: 4096 * 4096,
        maxParallelUploads: 2,
        acceptedFileTypes: ['image/png', 'image/jpeg', 'image/gif'],
        fileValidateTypeLabelExpectedTypesMap: {
          'image/jpeg': '.jpg',
          'image/gif': '.gif',
          'image/png': '.png'
        },
        instantUpload: true,
        allowMinimumUploadDuration: false,

        server: {

          // upload the file to the server
          process: (fieldName, file, metadata, load, error, progress, abort, transfer, options) => {
            // fieldName is the name of the input field
            // file is the actual file object to send
            const formData = new FormData()
            formData.append('name', file.name)
            formData.append('form_id', formId)
            formData.append('field_id', fieldId)
            formData.append('gform_unique_id', $uid.val())
            formData.append('original_filename', file.name)
            formData.append('file', file)
            formData.append('_gform_file_upload_nonce_' + formId, nonce)

            try {
              const request = new XMLHttpRequest()
              request.open('POST', imageEndpoint)

              // Should call the progress method to update the progress to 100% before calling load
              // Setting computable to false switches the loading indicator to infinite mode
              request.upload.onprogress = (e) => {
                progress(e.lengthComputable, e.loaded, e.total)
              }

              // Should call the load method when done and pass the returned server file id
              // this server file id is then used later on when reverting or restoring a file
              // so your server knows which file to return without exposing that info to the client
              request.onload = function () {
                if (request.status >= 200 && request.status < 300) {
                  // the load method accepts either a string (id) or an object
                  const jsonResponse = JSON.parse(request.responseText)

                  if (jsonResponse.status === 'ok') {
                    addOrUpdateFile(formId, fieldId, file, {
                      temp_filename: jsonResponse.data.temp_filename,
                      uploaded_filename: jsonResponse.data.uploaded_filename
                    })

                    load(jsonResponse.data.temp_filename)
                  } else {
                    error('error')
                  }
                } else {
                  // Can call the error method if something is wrong, should exit after
                  error('error')
                }
              }

              request.send(formData)

              // Should expose an abort method so the request can be cancelled
              return {
                abort: () => {
                  // This function is entered if the user has tapped the cancel button
                  request.abort()

                  // Let FilePond know the request has been cancelled
                  abort()
                }
              }
            } catch (exception) {
              error('error')
            }
          },
          // delete a limbo file (tmp upload)
          revert: (uniqueFileId, load, error) => {
            const formData = new FormData()
            formData.append('form_id', formId)
            formData.append('field_id', fieldId)
            formData.append('file', uniqueFileId)
            formData.append('_gform_file_upload_nonce_' + formId, nonce)

            try {
              const request = new XMLHttpRequest()
              request.open('POST', imageEndpoint + '&ih_page=revert')
              request.responseType = 'blob'

              request.onload = function () {
                if (request.status >= 200 && request.status < 300) {
                  deleteFile(formId, fieldId, uniqueFileId)
                  load()
                } else {
                  error('error')
                }
              }

              request.send(formData)
            } catch (exception) {
              error('error')
            }
          },
          // display a limbo (tmp) file
          restore: (uniqueFileId, load, error, progress, abort, headers) => {
            const formData = new FormData()
            formData.append('form_id', formId)
            formData.append('field_id', fieldId)
            formData.append('file', uniqueFileId)
            formData.append('_gform_file_upload_nonce_' + formId, nonce)

            try {
              const request = new XMLHttpRequest()
              request.open('POST', imageEndpoint + '&ih_page=restore')
              request.responseType = 'blob'

              // Should call the progress method to update the progress to 100% before calling load
              // Setting computable to false switches the loading indicator to infinite mode
              request.upload.onprogress = (e) => {
                progress(e.lengthComputable, e.loaded, e.total)
              }

              request.onload = function () {
                if (request.status >= 200 && request.status < 300) {
                  const blob = request.response
                  blob.name = request.getResponseHeader('content-disposition').split('filename="')[1].split('"')[0]
                  load(blob)
                } else {
                  error('error')
                }
              }

              request.send(formData)

              // Should expose an abort method so the request can be cancelled
              return {
                abort: () => {
                  // This function is entered if the user has tapped the cancel button
                  request.abort()

                  // Let FilePond know the request has been cancelled
                  abort()
                }
              }
            } catch (exception) {
              error('error')
            }
          },

          // display a local (already uploaded/saved) file
          load: (uniqueFileId, load, error, progress, abort, headers) => {
            try {
              const request = new XMLHttpRequest()
              request.open('GET', uniqueFileId)
              request.responseType = 'blob'

              request.onload = function () {
                if (request.status >= 200 && request.status < 300) {
                  load(request.response)
                } else {
                  error('error')
                }
              }

              request.send()
            } catch (exception) {
              error('error')
            }
          },

          // dont fetch from external sources
          fetch: null,

          // delete a local file securely
          remove: (uniqueFileId, load, error) => {
            const formData = new FormData()
            formData.append('form_id', formId)
            formData.append('field_id', fieldId)
            formData.append('entry_id', entryId)
            formData.append('file', uniqueFileId)
            formData.append('_gform_file_upload_nonce_' + formId, nonce)

            const $gravityviewNonce = $form.find('#is_gv_edit_entry')
            if ($gravityviewNonce.length > 0) {
              formData.append('is_gv_edit_entry', $gravityviewNonce.val())
              formData.append('gvid', getUrlParameter('gvid'))
            }

            const $wooGfKey = $form.find('input[name="wc_gforms_previous_cart_item_key"]')
            if ($wooGfKey.length > 0) {
              formData.append('wc_cart_key', $wooGfKey.val())
              formData.append('product_id', $form.find('input[name="product_id"]').val())
            }

            try {
              const request = new XMLHttpRequest()
              request.open('POST', imageEndpoint + '&ih_page=delete')
              request.responseType = 'blob'

              request.onload = function () {
                if (request.status >= 200 && request.status < 300) {
                  deleteFile(formId, fieldId, uniqueFileId)
                  load()
                } else {
                  error('error')
                }
              }

              request.send(formData)
            } catch (exception) {
              error('error')
            }
          }
        },
        beforeRemoveFile: (item) => {
          if (item.origin !== FilePond.FileOrigin.LOCAL) {
            return true
          }

          if (confirm(l10n.labelDeleteFileConfirm)) {
            return true
          }

          return false
        },

        // Internationalization
        labelIdle: l10n.labelIdleIntro + ' <span class="filepond--label-action"> ' + l10n.labelIdleButton + ' </span>',
        labelInvalidField: l10n.labelInvalidField,
        labelFileWaitingForSize: l10n.labelFileWaitingForSize,
        labelFileSizeNotAvailable: l10n.labelFileSizeNotAvailable,
        labelFileLoading: l10n.labelFileLoading,
        labelFileLoadError: l10n.labelFileLoadError,
        labelFileProcessing: l10n.labelFileProcessing,
        labelFileProcessingComplete: l10n.labelFileProcessingComplete,
        labelFileProcessingAborted: l10n.labelFileProcessingAborted,
        labelFileProcessingError: l10n.labelFileProcessingError,
        labelFileProcessingRevertError: l10n.labelFileProcessingRevertError,
        labelFileRemoveError: l10n.labelFileRemoveError,
        labelTapToCancel: l10n.labelTapToCancel,
        labelTapToRetry: l10n.labelTapToRetry,
        labelTapToUndo: l10n.labelTapToUndo,
        labelButtonRemoveItem: l10n.labelButtonRemoveItem,
        labelButtonAbortItemLoad: l10n.labelButtonAbortItemLoad,
        labelButtonRetryItemLoad: l10n.labelButtonRetryItemLoad,
        labelButtonAbortItemProcessing: l10n.labelButtonAbortItemProcessing,
        labelButtonUndoItemProcessing: l10n.labelButtonUndoItemProcessing,
        labelButtonRetryItemProcessing: l10n.labelButtonRetryItemProcessing,
        labelButtonProcessItem: l10n.labelButtonProcessItem,
        labelMaxFileSizeExceeded: l10n.labelMaxFileSizeExceeded,
        labelMaxFileSize: l10n.labelMaxFileSize,
        labelMaxTotalFileSizeExceeded: l10n.labelMaxTotalFileSizeExceeded,
        labelMaxTotalFileSize: l10n.labelMaxTotalFileSize,
        labelFileTypeNotAllowed: l10n.labelFileTypeNotAllowed,
        fileValidateTypeLabelExpectedTypes: l10n.fileValidateTypeLabelExpectedTypes,
        imageValidateSizeLabelFormatError: l10n.imageValidateSizeLabelFormatError,
        imageValidateSizeLabelImageSizeTooSmall: l10n.imageValidateSizeLabelImageSizeTooSmall,
        imageValidateSizeLabelImageSizeTooBig: l10n.imageValidateSizeLabelImageSizeTooBig,
        imageValidateSizeLabelExpectedMinSize: l10n.imageValidateSizeLabelExpectedMinSize,
        imageValidateSizeLabelExpectedMaxSize: l10n.imageValidateSizeLabelExpectedMaxSize,
        imageValidateSizeLabelImageResolutionTooLow: l10n.imageValidateSizeLabelImageResolutionTooLow,
        imageValidateSizeLabelImageResolutionTooHigh: l10n.imageValidateSizeLabelImageResolutionTooHigh,
        imageValidateSizeLabelExpectedMinResolution: l10n.imageValidateSizeLabelExpectedMinResolution,
        imageValidateSizeLabelExpectedMaxResolution: l10n.imageValidateSizeLabelExpectedMaxResolution
      }, FilePond, $form, currentPage, formId, fieldId, entryId, files, this)

      const pondObject = FilePond.create(this, config)
      pondStore[formId].push(pondObject)

      const pond = $('#field_' + formId + '_' + fieldId).find('.filepond--root')[0]
      const pondParentElement = pond.parentElement

      if (pond === undefined) {
        return
      }

      // Add in size of images based on container
      setImageSize(pond)
      const pondResizeObserver = new ResizeObserver(entries => {
        window.requestAnimationFrame(() => setImageSize(pond))
      })

      pondResizeObserver.observe(pond)

      gform.doAction('image_hopper_post_filepond_create', FilePond, $form, currentPage, formId, fieldId, entryId, files, this)

      // Feedback on action (visible on screen)
      $(pond).after('<div class="ih-feedback-messages" aria-live="polite" />')

      // Screen reader only live feedback on action (not visible on screen)
      $(pond).after('<div class="ih-screenreader-feedback-messages screen-reader-text" role="alert" tabindex="-1" />')

      // Screen reader Instructions for reordering images (not visible on screen)
      $(pond).after('<div id="ih-reorder-description" class="screen-reader-text">' + l10n.imageReorderInstructions + '</div>')

      // Prevent propagation of scroll event
      $(pond).on('change input', e => {
        return e.stopPropagation()
      })

      // Listen to keyboard navigation
      $(pond).on('keydown', e => keyboardNavigation(e, pond, pondObject, fieldId, formId))

      // Started file load
      pond.addEventListener('FilePond:addfilestart', e => {
        if (e.detail.file.origin === FilePond.FileOrigin.INPUT) {
          $(pond).parent().find('.maximum_files_reached').remove()

          addFile(formId, fieldId, {
            temp_filename: '',
            uploaded_filename: e.detail.file.filename,
            status: 'placeholder'
          })

          pendingUploads++
        }
      })

      // File loaded without errors
      pond.addEventListener('FilePond:addfile', e => {
        // Ensure all images are tabbable
        addTabIndex(e)

        if (e.detail.file.origin !== FilePond.FileOrigin.LOCAL) {

          if (!$(this).data('min-image-warning') || e.detail.error !== null) {
            return
          }

          const minWidth = $(this).data('min-image-warning-width')
          const minHeight = $(this).data('min-image-warning-height')

          // Display minimum width/height warning, if needed
          getImageSize(e.detail.file.file).then(({ width, height }) => {
            maybeAddMinimumWarningMessage(pond, e.detail.file.id, e.detail.file.filename, width, height, minWidth, minHeight)
          })
        }
      })

      pond.addEventListener('FilePond:reorderfiles', e => {
        const { target } = e.detail
        const files = e.detail.pond.getFiles()

        reorderFiles(files, fieldId, formId)

        // Reorder in the DOM so the tab order is consistent
        files.forEach(item => $(e.target).find('.filepond--list').prepend($(e.target).find('#filepond--item-' + item.id)))
        const list = e.target.querySelector('ul.filepond--list')
        Array.from(list.children).reverse().forEach(element => list.appendChild(element))

        $form.trigger('change')

        if (files[target] === undefined) {
          return
        }

        // Screen reader feedback
        $(pond)
          .parent()
          .find('.ih-screenreader-feedback-messages')
          .text(
            files[target].filename + '. ' +
            l10n.imageReorderActiveFeedback
              .replace('%1$s', (target + 1))
              .replace('%2$s', files.length)
          )
      })

      pond.addEventListener('FilePond:preparefile', e => {
        if (!$(this).data('min-image-warning') || e.detail.error) {
          return
        }

        const minWidth = $(this).data('min-image-warning-width')
        const minHeight = $(this).data('min-image-warning-height')

        // Display minimum width/height warning, if needed
        getImageSize(e.detail.output).then(({ width, height }) => {
          removeMinimumWarningMessage(pond, e.detail.file.id)
          maybeAddMinimumWarningMessage(pond, e.detail.file.id, e.detail.file.filename, width, height, minWidth, minHeight)
        })
      })

      // Finished processing a file
      pond.addEventListener('FilePond:processfile', e => {
        $('#filepond--item-' + e.detail.file.id).trigger('focus')

        if (e.detail.file.origin !== FilePond.FileOrigin.LOCAL) {
          // Trigger change event for Gravity PDF Previewer
          $form.trigger('change')

          pendingUploads--
        }
      })

      // If error rehydrating the file origin, remove pending upload
      pond.addEventListener('FilePond:error', e => {
        if (e.detail.file.origin !== FilePond.FileOrigin.LOCAL) {
          removeMinimumWarningMessage(pond, e.detail.file.id)

          pendingUploads--
        }
      })

      pond.addEventListener('FilePond:processfileabort', e => {
        if (e.detail.file.origin !== FilePond.FileOrigin.LOCAL) {
          removeMinimumWarningMessage(pond, e.detail.file.id)

          pendingUploads--
        }
      })

      pond.addEventListener('FilePond:removefile', e => {
        if (e.detail.file.origin === FilePond.FileOrigin.INPUT) {
          // Trigger change event for Gravity PDF Previewer
          $form.trigger('change')

          removeMinimumWarningMessage(pond, e.detail.file.id)
        }
      })

      // Check for max files warning
      pond.addEventListener('FilePond:warning', e => {
        if (e.detail.error.body === 'Max files' && !pondParentElement.querySelector('.maximum_files_reached')) {
          $(pond).parent().find('.ih-feedback-messages').append(`<div class="validation_message maximum_files_reached">${l10n.imageValidationMaxFilesReached.replace('%s', $(this).data('max-files'))}</div>`)
        }
      })
    })

    // Prevent form submission while the upload process is still in progress
    function preventSubmissionIfStillUploading (event) {
      const formId = event.data.formId

      if (pendingUploads > 0) {
        alert(l10n.labelFormSubmitWaitForUpload)
        window['gf_submitting_' + formId] = false
        $('#gform_ajax_spinner_' + formId).remove()

        return false
      }
    }
  }
})(jQuery, gform)
